import { z } from 'zod';

import { serviceTypeEnum } from './resourceManagementSchema.ts';

export const DashboardDateRangeSchema = z.object({
  datefrom: z.string(),
  dateto: z.string(),
  locationcode: z.string().optional(),
});

export const DashboardGetReportsRequestSchema = DashboardDateRangeSchema.extend(
  {
    serviceLocation: z.object({ id: z.uuid() }).optional(),
  }
);

export const DashboardOrdersByDayEnum = z.enum(['bookings', 'sales']);

export const DashboardGetOrdersByStatusRequestSchema =
  DashboardGetReportsRequestSchema.extend({
    ordersByDayType: DashboardOrdersByDayEnum,
  });

// Orders
export const DashboardOrdersByStatusSchema = z.object({
  orderDate: z.string().optional(),
  locationCode: z.string(),
  locationName: z.string().optional(),
  status: z.string().optional(),
  numberOfOrders: z.number().optional(),
  value: z.number().optional(),
  orderTireCount: z.number().optional(),
  billedOrderTireCount: z.number().optional(),
  orderPremiumTireCount: z.number().optional(),
  billedOrderPremiumTireCount: z.number().optional(),
  serviceSalesInOrder: z.number().optional(),
  serviceSalesBilled: z.number().optional(),
  cashPayment: z.number().optional(),
  cardPayment: z.number().optional(),
  keyDropPayment: z.number().optional(),
  giftCardPayment: z.number().optional(),
  totalOtherPayments: z.number().optional(),
  totalRefunds: z.number().optional(),
  orderDetailId: z.string(),
  customerOrderNumber: z.string().optional(),
  orderCreatedDate: z.string().optional(),
  currentOrderStatus: z.string().optional(),
  orderValue: z.number().optional(),
  vehicleLicense: z.string().optional(),
  vehicleVin: z.string().optional(),
  customerFirstname: z.string().optional(),
  customerLastname: z.string().optional(),
  refunds: z.number().optional(),
});

export const DashboardOrdersByHourSchema = z.object({
  datecreated: z.string().optional(),
  servicelocationid: z.string(),
  servicelocationname: z.string().optional(),
  houroforder: z.string(),
  numberoforders: z.number().nonnegative(),
  orderdate: z.string().optional(),
  status: z.string().optional(),
});

export const DashboardOrdersByHourWithLocationName =
  DashboardOrdersByHourSchema.extend({
    serviceLocationName: z.string(),
  });

// Jobs
export const DashboardJobsByTypeSchema = z.object({
  orderdetailid: z.uuid(),
  customerordernumber: z.string(),
  orderjobid: z.uuid(),
  jobreference: z.string(),
  locationid: z.uuid(),
  locationcode: z.string(),
  jobtype: z.string(),
  jobdatecreated: z.coerce.date<Date>(),
});

export const DashboardJobsByFulfilmentSchema = z.object({
  datecreated: z.string(),
  servicelocationid: z.string(),
  fulfilmenttype: serviceTypeEnum.optional(),
  numberofjobs: z.number().nonnegative(),
});

export const DashboardJobsByTechnicianSchema = z.object({
  orderdetailid: z.uuid(),
  customerordernumber: z.string(),
  orderjobid: z.uuid(),
  jobreference: z.string(),
  jobstatus: z.string(),
  locationid: z.uuid(),
  locationcode: z.string(),
  jobdatecreated: z.coerce.date<Date>(),
  jobdatescheduled: z.coerce.date<Date>(),
  technicianuserid: z.uuid(),
  technicianname: z.string(),
});

// Inventory
export const DashboardInventoryPopularJobsSchema = z.object({
  orderdetailid: z.uuid(),
  orderjobid: z.uuid(),
  jobdatecreated: z.coerce.date<Date>(),
  locationid: z.uuid(),
  locationcode: z.string(),
  jobdescription: z.string(),
});
export const DashboardInventoryPopularPartsSchema = z.object({
  orderdetailid: z.uuid(),
  orderjobid: z.uuid(),
  itempartid: z.uuid(),
  locationid: z.uuid(),
  locationcode: z.string(),
  createddate: z.coerce.date<Date>(),
  partnumber: z.string().nullish(),
  partdescription: z.string(),
});

// Fitting Status

export const DashboardFittingStatusProgressTypeEnum = z.enum([
  'awaiting',
  'inProgress',
  'complete',
]);

export const DashboardFittingStatusJobStatusEnum = z.enum([
  'Not Started',
  'In Progress',
  'Cancelled',
  'Awaiting',
  'Running Late',
  'Completed',
]);

export const DashboardFittingStatusJobSchema = z.object({
  key: z.string(),
  jobStatus: DashboardFittingStatusJobStatusEnum,
  technician: z.string(),
  jobId: z.uuid(),
  jobReference: z.string(),
  timeSlotStart: z.coerce.date<Date>(),
  timeSlotEnd: z.coerce.date<Date>(),
  timeSlotLength: z.string(),
  jobStarted: z.coerce.date<Date>().nullish().optional(),
  jobStartedStatus: z.string().nullish(),
  jobEnded: z.coerce.date<Date>().nullish().optional(),
  jobEndedStatus: z.string().nullish(),
  timeSpent: z.string().nullish(),
  fittedItems: z.string().nullish(),
  plannedStartEnd: z.string(),
  itemDescription: z.string().nullish(),
});

export const DashboardFittingStatusFESchema = z.object({
  dateScheduled: z.coerce.date<Date>(),
  serviceLocationId: z.uuid(),
  serviceLocationName: z.string(),
  fulfilmentType: z.string(),
  assetRef: z.string(),
  orderFittingJobs: z.string(),
  orderFittingJobsStatus: z.string().nullish(),
  orderNumber: z.string(),
  orderStatus: z.string(),
  bookingRef: z.string(),
  jobs: DashboardFittingStatusJobSchema.array(),
  orderId: z.uuid(),
});

export const DashboardOrderStatusEnum = z.enum([
  'Quoted',
  'Ready',
  'In Progress',
  'Invoiced',
  'Completed',
  'Cancelled',
]);

// Types
export type DashboardGetReportsRequestModel = z.infer<
  typeof DashboardGetReportsRequestSchema
>;

export type DashboardOrdersByStatusModel = z.infer<
  typeof DashboardOrdersByStatusSchema
>;
export type DashboardOrdersByHourModel = z.infer<
  typeof DashboardOrdersByHourSchema
>;
export type DashboardOrdersByHourWithLocationNameModel = z.infer<
  typeof DashboardOrdersByHourWithLocationName
>;

export type DashboardJobsByTypeModel = z.infer<
  typeof DashboardJobsByTypeSchema
>;
export type DashboardJobsByFulfilmentModel = z.infer<
  typeof DashboardJobsByFulfilmentSchema
>;
export type DashboardJobsByTechnicianModel = z.infer<
  typeof DashboardJobsByTechnicianSchema
>;

export type DashboardInventoryPopularJobsModel = z.infer<
  typeof DashboardInventoryPopularJobsSchema
>;
export type DashboardInventoryPopularPartsModel = z.infer<
  typeof DashboardInventoryPopularPartsSchema
>;

export type DashboardFittingStatusJobModel = z.infer<
  typeof DashboardFittingStatusJobSchema
>;
export type DashboardFittingStatusFEModel = z.infer<
  typeof DashboardFittingStatusFESchema
>;
export type DashboardFittingStatusJobStatusType = z.infer<
  typeof DashboardFittingStatusJobStatusEnum
>;

export type DashboardOrderStatusType = z.infer<typeof DashboardOrderStatusEnum>;
export type DashboardOrdersByDayType = z.infer<typeof DashboardOrdersByDayEnum>;
export type DashboardGetOrdersByStatusRequestModel = z.infer<
  typeof DashboardGetOrdersByStatusRequestSchema
>;
